import { useEffect, useState } from 'react';
import { Download, X } from 'lucide-react';
import { FaktLogo } from '@/components/brand';

/**
 * PWA-Install-Prompt-Banner.
 *
 * Faengt das nicht-standard-aber-de-facto-Standard `beforeinstallprompt`-
 * Event ab (Chrome/Edge auf Desktop und Android) und zeigt einen dezenten
 * Banner unten rechts an. iOS Safari liefert dieses Event NICHT — dort
 * gibt es keine programmatische Install-API; iPhone-Anwender muessen
 * "Zum Home-Bildschirm hinzufuegen" manuell aus dem Share-Sheet aufrufen.
 *
 * Design-Entscheidungen:
 * - Banner erscheint erst nach 3 Sekunden — nicht direkt beim Login,
 *   damit der User erst die App sieht und versteht, was er installiert
 * - Dismiss landet in localStorage; danach 30 Tage Ruhe (kein Spam)
 * - Wenn die App schon im Standalone-Modus laeuft (display-mode: standalone)
 *   wird der Banner gar nicht erst gemounted
 */

const DISMISS_KEY = 'fakt:install-prompt:dismissed';
const DISMISS_DAYS = 30;

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  prompt(): Promise<void>;
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

function isRecentlyDismissed(): boolean {
  try {
    const ts = localStorage.getItem(DISMISS_KEY);
    if (!ts) return false;
    const ageMs = Date.now() - Number(ts);
    return ageMs < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    // localStorage kann in Privat-Modus blockiert sein → wir zeigen den
    // Banner dann halt jedes Mal, statt zu crashen
    return false;
  }
}

function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    // iOS-Safari setzt navigator.standalone (nicht-Standard, aber dokumentiert)
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

export function InstallPrompt() {
  const [event, setEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (isStandalone() || isRecentlyDismissed()) return;

    const handler = (e: Event) => {
      e.preventDefault();
      setEvent(e as BeforeInstallPromptEvent);
      // Banner mit kurzer Verzoegerung — User soll erst die App sehen
      const t = window.setTimeout(() => setShow(true), 3000);
      return () => window.clearTimeout(t);
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  if (!show || !event) return null;

  const handleInstall = async () => {
    setShow(false);
    await event.prompt();
    const result = await event.userChoice;
    if (result.outcome === 'accepted') {
      // Erfolgreich installiert — kein Banner mehr noetig
      try {
        localStorage.setItem(DISMISS_KEY, String(Date.now()));
      } catch {
        // ignore
      }
    }
    setEvent(null);
  };

  const handleDismiss = () => {
    setShow(false);
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      // ignore
    }
  };

  return (
    <div
      role="dialog"
      aria-label="App installieren"
      className="fixed bottom-4 right-4 z-50 hidden w-80 max-w-[calc(100vw-2rem)] border border-hair-strong bg-panel p-4 shadow-lg md:block"
    >
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Hinweis schließen"
        className="absolute right-2 top-2 inline-flex h-7 w-7 items-center justify-center text-ink-muted hover:bg-bg-alt hover:text-ink"
      >
        <X className="h-4 w-4" />
      </button>
      <div className="flex items-start gap-3 pr-6">
        <FaktLogo size={28} />
        <div className="flex-1">
          <div className="text-[13px] font-medium text-ink">Als App installieren</div>
          <div className="mt-1 text-[12px] text-ink-soft">
            avameo fakt im eigenen Fenster, mit Icon im Startmenü.
          </div>
          <button
            type="button"
            onClick={handleInstall}
            className="mt-3 inline-flex items-center gap-1.5 bg-avameo-primary px-3 py-1.5 text-[12px] font-medium text-white hover:opacity-90"
          >
            <Download className="h-3.5 w-3.5" />
            Installieren
          </button>
        </div>
      </div>
    </div>
  );
}
